/* eslint-disable @typescript-eslint/no-unused-vars */
import { PolicyApi } from '@Apis';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { AdvancedFeatureTypeEnum, PolicyType } from '@Types/app';
import { isInUIC } from '@Utils/common';
import i18nInst from 'src/i18n';
import {
  addEmails,
  addItems,
  checkActionByType,
  checkEmailFormat,
  checkExtension,
  checkNotification,
  checkToken,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
  showAddFileNameExceedListLimitNotification,
  showAddFileNamesErrNotification,
} from 'src/pages/Policy/utils/fns';
import {
  ActionEnum,
  ApplyBoundEnum,
  DdaActionKeys,
  DdaFilter,
  DdaNotifyTypeKeys,
  DdaSuspiciousTagKeys,
  EnableDisableEnum,
  EnableDisableStatus,
  ExchangeOnlinePolicyItem,
  Notification,
  PolicyFilters,
} from 'src/pages/Policy/utils/types';
import { ddaActionDataSelector } from './selectors';

export const createDdaSlice = (setPolicy: any, getPolicy: any, getAll: any) => {
  const { t } = i18nInst;
  return {
    setDdaState: (payload: Partial<DdaFilter>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ...payload,
        },
      }));
    },
    setDdaNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          notification: {
            ...state.tmDdafilters.notification,
            ...payload,
          },
        },
      }));
    },
    importDdaApprovedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const {
        tmDdafilters: { ddaApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importEmails = emails.filter((email) => email.length > 0);
      const existedEntries = ddaApprovedSenderEmailEntries
        .split(separator)
        .filter((entry: string) => entry.length > 0);
      // process exceedLimit
      if (
        importEmails.length + existedEntries.length >
        limitation.approvedListMaxCount
      ) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // add emails
      currentStore.addDdaApprovedSender(importEmails);
    },
    addDdaApprovedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const includeDomain = true;
      const separator = ';';
      const {
        tmDdafilters: { ddaApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const { errList, entriesStr, exceedListLimit } = addEmails(
        email,
        ddaApprovedSenderEmailEntries,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        limitation.approvedListMaxCount,
        true
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ddaApprovedSenderEmailEntries: entriesStr,
        },
      }));
      // process exceedLimit
      if (exceedListLimit) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
      }
      // process errorList
      if (errList.length) {
        console.error('addDdaApprovedSender error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeDdaApprovedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmDdafilters: { ddaApprovedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        ddaApprovedSenderEmailEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ddaApprovedSenderEmailEntries: result,
        },
      }));
    },
    importDdaApprovedFile: (files: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = '|';
      const {
        tmDdafilters: { ddaApprovedFileNameEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importFiles = files.filter((file) => file.length > 0);
      const existedEntries = ddaApprovedFileNameEntries
        .split(separator)
        .filter((entry: string) => entry.length > 0);
      // process exceedLimit
      if (
        importFiles.length + existedEntries.length >
        limitation.approvedListMaxCount
      ) {
        showAddFileNameExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // add files
      currentStore.addDdaApprovedFile(importFiles);
    },
    addDdaApprovedFile: (file: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmDdafilters: { ddaApprovedFileNameEntries },
      } = currentState;
      const {
        config: { fileNameMaxLength, approvedListMaxCount },
      } = currentStore;
      const separator = '|';
      const { errList, result } = addItems(
        file,
        ddaApprovedFileNameEntries,
        approvedListMaxCount,
        fileNameMaxLength,
        checkExtension,
        separator,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ddaApprovedFileNameEntries: result,
        },
      }));
      // process errorList
      if (errList.length) {
        console.error('addDdaApprovedFile error list:', errList);
        showAddFileNamesErrNotification(errList);
      }
    },
    removeDdaApprovedFile: (file: string) => {
      const deleteFile = file.trim();
      if (deleteFile === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmDdafilters: { ddaApprovedFileNameEntries },
      } = currentState;
      const separator = '|';
      const result = removeItem(
        deleteFile,
        ddaApprovedFileNameEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ddaApprovedFileNameEntries: result,
        },
      }));
    },
    ddaActionHandler: (key: DdaActionKeys) => (action: ActionEnum) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          [key]: action,
        },
      }));
    },
    ddaNotifyTypeHandler: (key: DdaNotifyTypeKeys) => {
      const currentState = getPolicy();
      const originValue = currentState.tmDdafilters[key];
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          [key]: originValue ? 0 : 1,
        },
      }));
    },
    ddaSuspiciousTagHandler: (key: DdaSuspiciousTagKeys) => (value: string) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          [key]: value,
        },
      }));
    },
    setDdaTempQuarantineEnabled: (
      ddaTempQuarantineEnabled: EnableDisableStatus
    ) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmDdafilters: {
          ...state.tmDdafilters,
          ddaFileTempQuarantineEnabled: ddaTempQuarantineEnabled,
          ddaURLTempQuarantineEnabled: ddaTempQuarantineEnabled,
        },
      }));
    },
    checkAdvancedFeature: async () => {
      const currentStore = getAll();
      const {
        template: { policyType },
      } = currentStore;
      try {
        const { data } = await PolicyApi.getAdvancedFeatureInfo(
          AdvancedFeatureTypeEnum.enableUrlSandbox,
          policyType
        );
        return data;
      } catch (error) {
        console.error(error);
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    },
    setDdaUrlEnabled: async (ddaURLEnabled: EnableDisableStatus) => {
      const currentStore = getAll();
      const {
        tempVars: { wtpUnratedActionOriginal },
        setTempVars,
        template: { policyType },
      } = currentStore;
      let newWtpUnratedActionOriginal;
      if (isInUIC()) {
        // make a request to query whether to show the advanced tooltip
        const { data } = await PolicyApi.getAdvancedFeatureInfo(
          AdvancedFeatureTypeEnum.enableUrlSandbox,
          policyType
        );
        if (data.needConfirm) {
          setTempVars({ showAdvancedTooltip: true });
        }
      }
      setPolicy((state: ExchangeOnlinePolicyItem) => {
        // ddaURLEnabled enabled
        if (ddaURLEnabled === EnableDisableEnum.enabled) {
          // cache the original wtpUnratedAction and set wtpUnratedAction as 0
          newWtpUnratedActionOriginal =
            state.tmWebthreatprotections.wtpUnratedAction;
          state.tmWebthreatprotections.wtpUnratedAction = 0;
          // set ddaApplyBound
          if (
            state.tmWebthreatprotections.wtpApplyBound ===
            ApplyBoundEnum.incomming
          ) {
            state.tmDdafilters.ddaApplyBound = ApplyBoundEnum.incomming;
          }
        } else {
          // ddaURLEnabled disabled
          // set wtpUnratedAction as the original value
          state.tmWebthreatprotections.wtpUnratedAction =
            typeof wtpUnratedActionOriginal === 'undefined'
              ? state.tmWebthreatprotections.wtpUnratedAction
              : wtpUnratedActionOriginal;
        }
        state.tmDdafilters.ddaURLEnabled = ddaURLEnabled;
      });
      // set wtpUnratedActionOriginal
      if (typeof newWtpUnratedActionOriginal !== 'undefined') {
        setTempVars({
          wtpUnratedActionOriginal: newWtpUnratedActionOriginal,
        });
      }
    },
    validateVirtualAnalyzer: () => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const actionData = ddaActionDataSelector(currentStore);
      currentStore.replaceInvalidAction(actionData);

      const filter = currentState.tmDdafilters;
      const result = {
        [PolicyFilters.tmDdafilters]: {},
      };
      const { policyType } = filter;
      const { notifyMessageMaxLength, notifySubjectMaxLength } =
        currentStore.config.limitation;
      const actionKeys = Object.values(DdaActionKeys);
      const suspiciousTagKeys = Object.values(DdaSuspiciousTagKeys);
      let checkResult: any;
      // check action
      if (filter['ddaMonitorModeEnabled'] === EnableDisableEnum.disabled) {
        for (let i = 0; i < actionKeys.length; i++) {
          const key = actionKeys[i];
          const actionType = filter[key];
          if (actionType === ActionEnum.tagSubject) {
            // check suspicious tag
            const suspiciousTagValue = filter[suspiciousTagKeys[i]];
            checkResult = checkActionByType(suspiciousTagValue, actionType);
            if (checkResult) {
              // return directly when meet an error
              result[PolicyFilters.tmDdafilters] = {
                [suspiciousTagKeys[i]]: checkResult,
              };
              return result;
            }
          }
        }
      }
      // check notification
      checkResult = checkNotification(
        filter.notification,
        notifyMessageMaxLength,
        notifySubjectMaxLength
      );
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmDdafilters] = checkResult;
        return result;
      }
      // check token
      if (
        ![PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType)
      ) {
        checkToken({
          keyValue: filter.ddaReplacementText,
          keyName: 'ddaReplacementText',
        });
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmDdafilters] = checkResult;
          return result;
        }
      }
      return;
    },
    setDdaApplyBound: (payload: { [key: string]: ApplyBoundEnum }) => {
      const currentStore = getAll();
      const { setTempVars } = currentStore;
      if (typeof payload.ddaApplyBound !== 'undefined') {
        const ddaApplyBound = payload.ddaApplyBound;
        setTempVars({ ddaApplyBound });
        setPolicy((state: ExchangeOnlinePolicyItem) => {
          state.tmDdafilters.ddaApplyBound = ddaApplyBound;
        });
      }
    },
  };
};
